import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useIsAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldAlert, Save, ArrowLeft, ExternalLink, Trash2, AlertCircle } from "lucide-react";

/**
 * /ad/marketing/coaches/:coachId/edit — Full-fidelity admin editor.
 *
 * Platform admins can see and edit every top-level coach_profile field.
 * Structured sections cover the ~80% of fields with typed inputs; complex
 * nested lists (coaching_history, mentors, honors, ...) get JSON editors
 * for surgical manual cleanup.
 *
 * PUTs to /api/admin/coaches/{id}/full which enforces a server-side allowlist.
 */
export default function AdminCoachEdit() {
  const { coachId } = useParams();
  const isAdmin = useIsAdmin();
  const nav = useNavigate();

  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jsonErrors, setJsonErrors] = useState({});

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    api.get(`/admin/coaches/${coachId}`)
      .then(({ data }) => { setOrig(data); setForm(data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [coachId, isAdmin]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (parent, key, value) =>
    setForm((f) => ({ ...f, [parent]: { ...(f?.[parent] || {}), [key]: value } }));

  const dirty = useMemo(() => {
    if (!orig || !form) return false;
    return JSON.stringify(orig) !== JSON.stringify(form);
  }, [orig, form]);

  const save = async () => {
    if (Object.keys(jsonErrors).length) {
      toast.error("Fix JSON errors before saving");
      return;
    }
    setSaving(true);
    try {
      // Only send fields that changed to keep the diff tight in the audit log
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (JSON.stringify(form[k]) !== JSON.stringify(orig?.[k])) payload[k] = form[k];
      });
      if (Array.isArray(payload.club_experience)) {
        payload.club_experience = payload.club_experience.filter(
          (r) => (r.organization || "").trim() || (r.position || "").trim() || (r.notes || "").trim()
        );
      }
      if (!Object.keys(payload).length) {
        toast.info("Nothing changed");
        setSaving(false);
        return;
      }
      const { data } = await api.put(`/admin/coaches/${coachId}/full`, payload);
      setOrig(data.coach);
      setForm(data.coach);
      toast.success(
        `Saved · ${Object.keys(payload).length} field${Object.keys(payload).length === 1 ? "" : "s"} updated` +
        (data.dropped_fields?.length ? ` · ${data.dropped_fields.length} protected field(s) ignored` : "")
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Shell>
        <div className="p-10 border border-blaze text-blaze" data-testid="admin-edit-forbidden">
          Platform-admin only.
        </div>
      </Shell>
    );
  }
  if (loading || !form) return <Shell><div className="p-10 heading text-2xl">LOADING…</div></Shell>;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="admin-coach-edit-page">
        <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
          <ShieldAlert size={14} /> PLATFORM ADMIN · FULL COACH EDITOR
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
          <div>
            <h1 className="heading text-3xl md:text-4xl">{form.name || "(unnamed coach)"}</h1>
            <div className="text-neutral-500 text-xs mt-1">
              id <code className="text-neutral-300">{form.id}</code>
              {form.last_admin_edit_by && (
                <span className="ml-3">last edited by <span className="text-neutral-300">{form.last_admin_edit_by}</span> · {form.last_admin_edit_at?.slice(0, 16)?.replace("T", " ")}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/coaches/${coachId}`} data-testid="admin-edit-view-public" className="text-xs heading tracking-widest border border-neutral-800 px-3 py-2 hover:border-volt hover:text-volt flex items-center gap-1">
              <ExternalLink size={12} /> VIEW PUBLIC
            </Link>
            <Link to="/ad/marketing/coaches" data-testid="admin-edit-back-btn" className="text-xs heading tracking-widest border border-neutral-800 px-3 py-2 hover:border-volt hover:text-volt flex items-center gap-1">
              <ArrowLeft size={12} /> BACK TO LIST
            </Link>
          </div>
        </div>

        {/* Sticky save bar */}
        <div className={`sticky top-0 z-30 -mx-4 md:-mx-8 mt-6 px-4 md:px-8 py-3 flex items-center justify-between backdrop-blur-md border-b ${dirty ? "border-volt bg-[#0A101A]/95" : "border-neutral-900 bg-black/60"}`}>
          <div className="text-xs heading tracking-widest">
            {dirty ? <span className="text-volt">● UNSAVED CHANGES</span> : <span className="text-neutral-500">ALL SAVED</span>}
            {Object.keys(jsonErrors).length > 0 && (
              <span className="ml-3 text-blaze flex items-center gap-1 inline-flex"><AlertCircle size={12} /> {Object.keys(jsonErrors).length} JSON error(s)</span>
            )}
          </div>
          <button
            onClick={save}
            disabled={!dirty || saving || Object.keys(jsonErrors).length > 0}
            data-testid="admin-edit-save-btn"
            className="bg-blaze text-white heading text-sm tracking-widest px-5 py-2.5 hover:glow-blaze disabled:opacity-40 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? "SAVING…" : "SAVE ALL CHANGES"}
          </button>
        </div>

        {/* Identity */}
        <Section title="Identity" testid="section-identity">
          <Grid>
            <TextField label="Name" v={form.name} onChange={(v) => set("name", v)} tid="edit-name" />
            <TextField label="Headline" v={form.headline} onChange={(v) => set("headline", v)} tid="edit-headline" wide />
            <SelectField label="Sport" v={form.sport} options={["Lacrosse", "Football", "Basketball", "Baseball", "Soccer", "Volleyball"]} onChange={(v) => set("sport", v)} tid="edit-sport" />
            <SelectField label="Gender" v={form.gender} options={["Men", "Women", "Coed"]} onChange={(v) => set("gender", v)} tid="edit-gender" />
            <SelectField label="Primary Level" v={form.primary_level} options={["MS", "HS", "JUCO", "College"]} onChange={(v) => set("primary_level", v)} tid="edit-primary_level" />
            <TextField label="Location (City, State)" v={form.location} onChange={(v) => set("location", v)} tid="edit-location" />
            <TextField label="Email" v={form.email} onChange={(v) => set("email", v)} tid="edit-email" />
            <TextField label="Phone" v={form.phone} onChange={(v) => set("phone", v)} tid="edit-phone" />
            <TextField label="Headshot URL" v={form.headshot_url} onChange={(v) => set("headshot_url", v)} tid="edit-headshot_url" wide />
            <TextField label="Bio Source URL" v={form.bio_source_url} onChange={(v) => set("bio_source_url", v)} tid="edit-bio_source_url" wide />
          </Grid>
        </Section>

        {/* Current role */}
        <Section title="Current Position" testid="section-current-role">
          <Grid>
            <TextField label="Current Organization" v={form.current_organization} onChange={(v) => set("current_organization", v)} tid="edit-current_organization" />
            <TextField label="Current Role" v={form.current_role} onChange={(v) => set("current_role", v)} tid="edit-current_role" />
            <SelectField label="Current Division" v={form.current_division} options={["", "D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club", "HS Varsity", "JV", "Freshman", "Middle School"]} onChange={(v) => set("current_division", v)} tid="edit-current_division" />
            <TextField label="Current Conference" v={form.current_conference} onChange={(v) => set("current_conference", v)} tid="edit-current_conference" />
            <SelectField label="Claim Status" v={form.claim_status} options={["", "unclaimed", "claimed", "suspended", "deleted"]} onChange={(v) => set("claim_status", v)} tid="edit-claim_status" />
            <BoolField label="Is Verified (admin)" v={form.is_verified} onChange={(v) => set("is_verified", v)} tid="edit-is_verified" />
          </Grid>
        </Section>

        {/* Willingness */}
        <Section title="Job Willingness & Preferences" testid="section-willingness">
          <Grid>
            <BoolField label="Open to HS Jobs" v={form.open_to_hs_jobs} onChange={(v) => set("open_to_hs_jobs", v)} tid="edit-open_to_hs_jobs" />
            <BoolField label="Open to College Jobs" v={form.open_to_college_jobs} onChange={(v) => set("open_to_college_jobs", v)} tid="edit-open_to_college_jobs" />
            <TextField label="Desired Role" v={form.desired_role} onChange={(v) => set("desired_role", v)} tid="edit-desired_role" />
            <TextField label="Desired Salary Band" v={form.desired_salary_band} onChange={(v) => set("desired_salary_band", v)} tid="edit-desired_salary_band" />
            <SelectField label="Position Type Pref (HS)" v={form.position_type_pref} options={["", "full_time", "part_time", "either"]} onChange={(v) => set("position_type_pref", v)} tid="edit-position_type_pref" />
            <BoolField label="Open to Faculty Role" v={form.open_to_faculty} onChange={(v) => set("open_to_faculty", v)} tid="edit-open_to_faculty" />
            <StringListField label="Desired Roles" v={form.desired_roles} onChange={(v) => set("desired_roles", v)} tid="edit-desired_roles" wide />
            <StringListField label="Desired Divisions" v={form.desired_divisions} onChange={(v) => set("desired_divisions", v)} tid="edit-desired_divisions" wide />
            <StringListField label="Geographic Regions" v={form.geographic_regions} onChange={(v) => set("geographic_regions", v)} tid="edit-geographic_regions" wide />
            <StringListField label="Faculty Subjects" v={form.faculty_subjects} onChange={(v) => set("faculty_subjects", v)} tid="edit-faculty_subjects" wide />
          </Grid>
        </Section>

        {/* Narrative */}
        <Section title="Narrative" testid="section-narrative">
          <Grid>
            <TextAreaField label="Philosophy" v={form.philosophy} onChange={(v) => set("philosophy", v)} tid="edit-philosophy" rows={4} />
            <TextAreaField label="Aspirations" v={form.aspirations} onChange={(v) => set("aspirations", v)} tid="edit-aspirations" rows={3} />
            <StringListField label="Specialties" v={form.specialties} onChange={(v) => set("specialties", v)} tid="edit-specialties" wide />
            <StringListField label="Affiliations" v={form.affiliations} onChange={(v) => set("affiliations", v)} tid="edit-affiliations" wide />
            <StringListField label="Coach HOF Memberships" v={form.coach_hof_memberships} onChange={(v) => set("coach_hof_memberships", v)} tid="edit-coach_hof_memberships" wide />
            <TextAreaField label="Departure Conditions" v={form.departure_conditions} onChange={(v) => set("departure_conditions", v)} tid="edit-departure_conditions" rows={3} />
            <TextField label="MaxPreps URL" v={form.maxpreps_url} onChange={(v) => set("maxpreps_url", v)} tid="edit-maxpreps_url" wide />
          </Grid>
        </Section>

        {/* Playing history */}
        <Section title="Playing History" testid="section-playing-history">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <SubTitle>High School</SubTitle>
              <TextField label="HS Team" v={form.playing_history?.hs_team} onChange={(v) => setNested("playing_history", "hs_team", v)} tid="edit-ph_hs_team" />
              <TextField label="HS Location" v={form.playing_history?.hs_location} onChange={(v) => setNested("playing_history", "hs_location", v)} tid="edit-ph_hs_location" />
              <TextField label="HS State" v={form.playing_history?.hs_state} onChange={(v) => setNested("playing_history", "hs_state", v)} tid="edit-ph_hs_state" />
              <TextField label="HS Position" v={form.playing_history?.hs_position} onChange={(v) => setNested("playing_history", "hs_position", v)} tid="edit-ph_hs_position" />
              <TextField label="HS Coach" v={form.playing_history?.hs_coach} onChange={(v) => setNested("playing_history", "hs_coach", v)} tid="edit-ph_hs_coach" />
              <BoolField label="HS Coach in HoF" v={form.playing_history?.hs_coach_hof} onChange={(v) => setNested("playing_history", "hs_coach_hof", v)} tid="edit-ph_hs_coach_hof" />
              <TextField label="HS Club Team" v={form.playing_history?.hs_club_team} onChange={(v) => setNested("playing_history", "hs_club_team", v)} tid="edit-ph_hs_club_team" />
              <TextField label="HS Club Coach" v={form.playing_history?.hs_club_coach} onChange={(v) => setNested("playing_history", "hs_club_coach", v)} tid="edit-ph_hs_club_coach" />
              <NumberField label="HS Years Played" v={form.playing_history?.hs_years_played} onChange={(v) => setNested("playing_history", "hs_years_played", v)} tid="edit-ph_hs_years_played" />
              <BoolField label="HS Captain" v={form.playing_history?.hs_captain} onChange={(v) => setNested("playing_history", "hs_captain", v)} tid="edit-ph_hs_captain" />
              <BoolField label="HS Elite Team" v={form.playing_history?.hs_elite_team} onChange={(v) => setNested("playing_history", "hs_elite_team", v)} tid="edit-ph_hs_elite_team" />
              <BoolField label="HS All-American" v={form.playing_history?.hs_all_american} onChange={(v) => setNested("playing_history", "hs_all_american", v)} tid="edit-ph_hs_all_american" />
              <BoolField label="HS HoF" v={form.playing_history?.hs_hof} onChange={(v) => setNested("playing_history", "hs_hof", v)} tid="edit-ph_hs_hof" />
            </div>
            <div>
              <SubTitle>College</SubTitle>
              <TextField label="College Team" v={form.playing_history?.college_team} onChange={(v) => setNested("playing_history", "college_team", v)} tid="edit-ph_college_team" />
              <TextField label="College Coach" v={form.playing_history?.college_coach} onChange={(v) => setNested("playing_history", "college_coach", v)} tid="edit-ph_college_coach" />
              <TextField label="College Division" v={form.playing_history?.college_division} onChange={(v) => setNested("playing_history", "college_division", v)} tid="edit-ph_college_division" />
              <TextField label="College Position" v={form.playing_history?.college_position} onChange={(v) => setNested("playing_history", "college_position", v)} tid="edit-ph_college_position" />
              <BoolField label="College Captain" v={form.playing_history?.college_captain} onChange={(v) => setNested("playing_history", "college_captain", v)} tid="edit-ph_college_captain" />
              <BoolField label="College All-American" v={form.playing_history?.college_all_american} onChange={(v) => setNested("playing_history", "college_all_american", v)} tid="edit-ph_college_all_american" />
              <BoolField label="College HoF" v={form.playing_history?.college_hof} onChange={(v) => setNested("playing_history", "college_hof", v)} tid="edit-ph_college_hof" />
              <NumberField label="National Position Rank" v={form.playing_history?.national_position_rank} onChange={(v) => setNested("playing_history", "national_position_rank", v)} tid="edit-ph_national_position_rank" />
            </div>
            <div>
              <SubTitle>Professional</SubTitle>
              <BoolField label="Played Professionally" v={form.playing_history?.professional} onChange={(v) => setNested("playing_history", "professional", v)} tid="edit-ph_professional" />
              <TextField label="Pro Team" v={form.playing_history?.professional_team} onChange={(v) => setNested("playing_history", "professional_team", v)} tid="edit-ph_professional_team" />
              <StringListField label="Personal Accolades" v={form.playing_history?.accolades} onChange={(v) => setNested("playing_history", "accolades", v)} tid="edit-ph_accolades" />
              <StringListField label="Team Accolades" v={form.playing_history?.team_accolades} onChange={(v) => setNested("playing_history", "team_accolades", v)} tid="edit-ph_team_accolades" />
              <StringListField label="Pro Accolades" v={form.playing_history?.professional_accolades} onChange={(v) => setNested("playing_history", "professional_accolades", v)} tid="edit-ph_professional_accolades" />
            </div>
          </div>
        </Section>

        {/* Education */}
        <Section title="Education" testid="section-education">
          <Grid>
            <TextField label="Degree" v={form.education?.degree} onChange={(v) => setNested("education", "degree", v)} tid="edit-edu_degree" />
            <TextField label="School" v={form.education?.school} onChange={(v) => setNested("education", "school", v)} tid="edit-edu_school" />
            <NumberField label="GPA" step="0.01" v={form.education?.gpa} onChange={(v) => setNested("education", "gpa", v)} tid="edit-edu_gpa" />
            <BoolField label="Has Master's" v={form.education?.masters} onChange={(v) => setNested("education", "masters", v)} tid="edit-edu_masters" />
            <TextField label="Master's School" v={form.education?.masters_school} onChange={(v) => setNested("education", "masters_school", v)} tid="edit-edu_masters_school" wide />
          </Grid>
        </Section>

        {/* Privacy */}
        <Section title="Privacy" testid="section-privacy">
          <Grid>
            <BoolField label="Discoverable in Talent Pool" v={form.privacy?.discoverable} onChange={(v) => setNested("privacy", "discoverable", v)} tid="edit-priv_discoverable" />
            <BoolField label="Anonymous in Pool" v={form.privacy?.anonymous_in_pool} onChange={(v) => setNested("privacy", "anonymous_in_pool", v)} tid="edit-priv_anonymous_in_pool" />
            <BoolField label="Mask Current Employer" v={form.privacy?.mask_current_employer} onChange={(v) => setNested("privacy", "mask_current_employer", v)} tid="edit-priv_mask_current_employer" />
            <StringListField label="Hidden From Programs (names)" v={form.privacy?.hidden_from_program_names} onChange={(v) => setNested("privacy", "hidden_from_program_names", v)} tid="edit-priv_hidden_from_program_names" wide />
          </Grid>
        </Section>

        {/* Complex lists as JSON */}
        <Section title="Coaching History" testid="section-coaching-history" subtitle={`${(form.coaching_history || []).length} stint(s) · edit as JSON below`}>
          <JsonField
            v={form.coaching_history}
            onChange={(v) => set("coaching_history", v)}
            onError={(err) => setJsonErrors((e) => ({ ...e, coaching_history: err }))}
            onValid={() => setJsonErrors((e) => { const c = { ...e }; delete c.coaching_history; return c; })}
            tid="edit-coaching_history"
            rows={16}
          />
        </Section>

        <Section title="Mentors" testid="section-mentors" subtitle={`${(form.mentors || []).length} mentor(s)`}>
          <JsonField v={form.mentors} onChange={(v) => set("mentors", v)} onError={(err) => setJsonErrors((e) => ({ ...e, mentors: err }))} onValid={() => setJsonErrors((e) => { const c = { ...e }; delete c.mentors; return c; })} tid="edit-mentors" rows={8} />
        </Section>

        <Section title="Club Experience" testid="section-club-exp" subtitle="Structured — no JSON needed">
          <ClubExperienceEditor
            rows={form.club_experience || []}
            onChange={(v) => set("club_experience", v)}
          />
        </Section>

        <Section title="Honors" testid="section-honors" subtitle="One honor per line">
          <textarea
            data-testid="edit-honors"
            rows={6}
            value={(form.honors || []).map((h) => (typeof h === "string" ? h : JSON.stringify(h))).join("\n")}
            onChange={(e) => set("honors", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
            className="w-full text-sm bg-black border border-neutral-800 px-3 py-2 focus:border-volt outline-none"
            placeholder={"USILA Hall of Fame (2019)\n3x Conference Coach of the Year"}
          />
        </Section>

        {/* Danger zone */}
        <Section title="Danger Zone" testid="section-danger" danger>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-edit-soft-delete-btn"
              onClick={async () => {
                if (!window.confirm(`Soft-delete ${form.name}? It will be hidden from every listing.`)) return;
                try {
                  await api.delete(`/admin/coaches/${coachId}`);
                  toast.success("Coach soft-deleted");
                  nav("/ad/marketing/coaches");
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
              }}
              className="border border-blaze text-blaze text-xs heading tracking-widest px-4 py-2 hover:bg-blaze hover:text-white flex items-center gap-1"
            >
              <Trash2 size={12} /> SOFT DELETE
            </button>
            <button
              data-testid="admin-edit-hard-delete-btn"
              onClick={async () => {
                const phrase = window.prompt(`Type DELETE to permanently remove ${form.name}:`);
                if (phrase !== "DELETE") { toast.error("Cancelled"); return; }
                try {
                  await api.delete(`/admin/coaches/${coachId}?hard=true`);
                  toast.success("Coach permanently deleted");
                  nav("/ad/marketing/coaches");
                } catch (e) { toast.error(e.response?.data?.detail || "Hard delete failed"); }
              }}
              className="text-xs heading tracking-widest text-red-500 hover:text-red-400 px-4 py-2 flex items-center gap-1"
            >
              <Trash2 size={12} /> HARD DELETE (PERMANENT)
            </button>
          </div>
        </Section>

        {/* Bottom save button (mirrors sticky top for long forms) */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={save}
            disabled={!dirty || saving || Object.keys(jsonErrors).length > 0}
            data-testid="admin-edit-save-btn-bottom"
            className="bg-blaze text-white heading text-sm tracking-widest px-6 py-3 hover:glow-blaze disabled:opacity-40 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? "SAVING…" : "SAVE ALL CHANGES"}
          </button>
        </div>
      </div>
    </Shell>
  );
}

// ---- Layout primitives ---------------------------------------------------

function Shell({ children }) {
  return <div className="text-white">{children}</div>;
}

function Section({ title, subtitle, children, testid, danger }) {
  return (
    <div className={`mt-8 border ${danger ? "border-blaze/50" : "border-neutral-800"} bg-[#080A0F] p-5 md:p-6`} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className={`heading text-xl tracking-wider ${danger ? "text-blaze" : "text-white"}`}>{title.toUpperCase()}</h2>
          {subtitle && <div className="text-neutral-500 text-xs mt-1">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function SubTitle({ children }) {
  return <div className="text-[10px] tracking-widest text-volt mb-2">{children}</div>;
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

// ---- Field components ----------------------------------------------------

function TextField({ label, v, onChange, tid, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      <input
        data-testid={tid}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      />
    </div>
  );
}

function NumberField({ label, v, onChange, tid, step }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={tid}
        type="number"
        step={step || "1"}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      />
    </div>
  );
}

function TextAreaField({ label, v, onChange, tid, rows, wide = true }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label}</Label>
      <textarea
        data-testid={tid}
        rows={rows || 3}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      />
    </div>
  );
}

function BoolField({ label, v, onChange, tid }) {
  return (
    <label className="flex items-center gap-2 text-sm bg-black border border-neutral-800 px-3 py-2 hover:border-neutral-700 cursor-pointer">
      <input
        type="checkbox"
        data-testid={tid}
        checked={!!v}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-volt"
      />
      <span className="text-neutral-300">{label}</span>
    </label>
  );
}

function SelectField({ label, v, options, onChange, tid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select
        data-testid={tid}
        value={v ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      >
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </div>
  );
}

function StringListField({ label, v, onChange, tid, wide }) {
  // Comma-separated string input <-> list of strings
  const joined = Array.isArray(v) ? v.join(", ") : "";
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label>{label} <span className="text-neutral-600">(comma-separated)</span></Label>
      <input
        data-testid={tid}
        value={joined}
        onChange={(e) => {
          const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
          onChange(arr);
        }}
        className="w-full mt-1 bg-black border border-neutral-800 px-3 py-2 text-sm focus:border-volt outline-none"
      />
    </div>
  );
}

function JsonField({ v, onChange, onError, onValid, tid, rows }) {
  const [text, setText] = useState(() => JSON.stringify(v ?? [], null, 2));
  const [err, setErr] = useState(null);

  // If parent replaces the value (e.g. after save), resync
  useEffect(() => {
    setText(JSON.stringify(v ?? [], null, 2));
    setErr(null);
    onValid?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(v)]);

  const handle = (t) => {
    setText(t);
    try {
      const parsed = JSON.parse(t);
      setErr(null);
      onValid?.();
      onChange(parsed);
    } catch (e) {
      setErr(e.message);
      onError?.(e.message);
    }
  };

  return (
    <div>
      <textarea
        data-testid={tid}
        rows={rows || 10}
        value={text}
        onChange={(e) => handle(e.target.value)}
        spellCheck={false}
        className={`w-full font-mono text-xs bg-black border ${err ? "border-blaze" : "border-neutral-800"} px-3 py-2 focus:border-volt outline-none`}
      />
      {err && <div className="text-blaze text-xs mt-1 flex items-center gap-1"><AlertCircle size={12} /> {err}</div>}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[10px] tracking-widest text-neutral-500">{children}</label>;
}

// Structured editor for club_experience rows — replaces the raw JSON box that
// rejected plain text ("Founder of..." is not valid JSON) and blocked saves.
function ClubExperienceEditor({ rows, onChange }) {
  const upd = (i, key, value) => onChange(rows.map((r, ix) => (ix === i ? { ...r, [key]: value } : r)));
  const num = (x) => (x === "" ? null : Number(x) || null);
  return (
    <div className="space-y-3">
      {rows.map((r, i) => (
        <div key={i} className="border border-neutral-800 bg-black/40 p-3" data-testid={`club-exp-row-${i}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>ORGANIZATION</Label><input className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-org-${i}`} value={r.organization || ""} onChange={(e) => upd(i, "organization", e.target.value)} placeholder="e.g. Memphis Elite LC" /></div>
            <div><Label>POSITION</Label><input className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-position-${i}`} value={r.position || ""} onChange={(e) => upd(i, "position", e.target.value)} placeholder="Founder / Director / Coach" /></div>
            <div><Label>AGE GROUP</Label><input className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-age-${i}`} value={r.age_group || ""} onChange={(e) => upd(i, "age_group", e.target.value)} placeholder="U15 / HS" /></div>
            <div><Label># PLAYERS</Label><input type="number" className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-players-${i}`} value={r.num_players ?? ""} onChange={(e) => upd(i, "num_players", num(e.target.value))} /></div>
            <div><Label>START YEAR</Label><input type="number" className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-start-${i}`} value={r.start_year ?? ""} onChange={(e) => upd(i, "start_year", num(e.target.value))} /></div>
            <div><Label>END YEAR (blank = current)</Label><input type="number" className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-end-${i}`} value={r.end_year ?? ""} onChange={(e) => upd(i, "end_year", num(e.target.value))} /></div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
                <input type="checkbox" data-testid={`club-exp-director-${i}`} checked={!!r.director} onChange={(e) => upd(i, "director", e.target.checked)} /> Director-level
              </label>
            </div>
            <div className="flex items-end justify-end pb-1">
              <button data-testid={`club-exp-remove-${i}`} onClick={() => onChange(rows.filter((_, ix) => ix !== i))} className="text-[10px] tracking-widest border border-neutral-700 text-neutral-500 px-2 py-1 hover:border-blaze hover:text-blaze">REMOVE</button>
            </div>
          </div>
          <div className="mt-2"><Label>NOTES</Label><input className="w-full text-sm bg-black border border-neutral-800 px-2 py-1.5 focus:border-volt outline-none" data-testid={`club-exp-notes-${i}`} value={r.notes || ""} onChange={(e) => upd(i, "notes", e.target.value)} placeholder="e.g. Founded the program; grew it to 3 teams" /></div>
        </div>
      ))}
      <button
        data-testid="club-exp-add-btn"
        onClick={() => onChange([...(rows || []), { organization: "", position: "", age_group: "", director: false, num_players: null, start_year: null, end_year: null, notes: "" }])}
        className="text-[10px] tracking-widest border border-volt text-volt px-3 py-1.5 hover:bg-volt hover:text-black"
      >+ ADD CLUB EXPERIENCE</button>
    </div>
  );
}
